// src/components/ui/notifications/showToast.tsx
import toast from "react-hot-toast";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Bell } from "lucide-react";
import { useState, useEffect } from "react";

interface ShowToastOptions {
  title: string;
  description?: string;
  type?: "success" | "error" | "warning" | "info";
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
  showCheckbox?: boolean;
  checkboxKey?: string;
}

const toastConfig = {
  success: {
    icon: CheckCircle,
    bg: "from-secondary/80 to-secondary/60",
    border: "border-secondary/50",
    text: "text-white",
    iconColor: "text-accent",
    progress: "bg-accent",
  },
  error: {
    icon: AlertCircle,
    bg: "from-red-500/80 to-red-600/80",
    border: "border-red-400/50",
    text: "text-white",
    iconColor: "text-white",
    progress: "bg-white",
  },
  warning: {
    icon: AlertTriangle,
    bg: "from-amber-500/80 to-amber-600/80",
    border: "border-amber-400/50",
    text: "text-white",
    iconColor: "text-white",
    progress: "bg-white",
  },
  info: {
    icon: Bell,
    bg: "from-black/80 to-black/60",
    border: "border-accent/30",
    text: "text-white",
    iconColor: "text-accent",
    progress: "bg-accent",
  },
};

const ToastContent = ({
  t,
  title,
  description,
  actionLabel,
  onAction,
  duration,
  showCheckbox,
  checkboxKey,
  config,
  Icon,
}: any) => {
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(duration / 1000));
  const [isChecked, setIsChecked] = useState(false);

  const handleClose = () => toast.dismiss(t.id);

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleClose();
      return;
    }
    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsChecked(checked);
    if (checked && checkboxKey) {
      localStorage.setItem(checkboxKey, "true");
      // Petit toast d'annulation rapide (optionnel)
      toast(
        (tUndo) => (
          <div className="flex items-center gap-2 text-sm bg-black/90 backdrop-blur-md text-white rounded-xl px-3 py-2 shadow-lg">
            <span>Rappel désactivé</span>
            <button
              onClick={() => {
                localStorage.removeItem(checkboxKey);
                toast.dismiss(tUndo.id);
                toast.success("Rappel réactivé");
              }}
              className="text-accent font-medium hover:underline"
            >
              Annuler
            </button>
          </div>
        ),
        { duration: 5000, position: "top-right" }
      );
    }
  };

  return (
    <div
      className={`
        relative max-w-md w-full
        transform transition-all duration-300 ease-out
        ${t.visible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
      `}
    >
      <div className={`rounded-2xl shadow-2xl overflow-hidden bg-gradient-to-r ${config.bg} border ${config.border} backdrop-blur-sm`}>
        {/* Barre de progression */}
        <div className="relative h-1 w-full bg-white/20">
          <div
            className={`absolute inset-0 ${config.progress} rounded-full transition-all duration-1000 ease-linear`}
            style={{ width: `${(secondsLeft / (duration / 1000)) * 100}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <Icon size={20} className={config.iconColor} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className={`font-semibold ${config.text}`}>{title}</h4>
                <button
                  onClick={handleClose}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
              {description && (
                <p className={`text-sm ${config.text} opacity-80 mt-1 leading-relaxed`}>{description}</p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                {actionLabel && (
                  <button
                    onClick={() => {
                      onAction?.();
                      handleClose();
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm ${config.text} hover:bg-white/30 transition`}
                  >
                    {actionLabel}
                  </button>
                )}
                {showCheckbox && checkboxKey && (
                  <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        className="sr-only"
                      />
                      <div
                        className={`
                          w-4 h-4 rounded border-2 flex items-center justify-center
                          transition-all duration-200
                          ${isChecked
                            ? "bg-accent border-accent"
                            : "border-white/50 bg-transparent group-hover:border-accent"
                          }
                        `}
                      >
                        {isChecked && <CheckCircle size={10} className="text-black" />}
                      </div>
                    </div>
                    <span className="group-hover:text-white">Ne plus me rappeler</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const showToast = ({
  title,
  description,
  type = "info",
  actionLabel,
  onAction,
  duration = 5000,
  showCheckbox = false,
  checkboxKey,
}: ShowToastOptions) => {
  // Vérification de la préférence
  if (checkboxKey && localStorage.getItem(checkboxKey) === "true") return;

  const config = toastConfig[type];
  const Icon = config.icon;

  toast.custom(
    (t) => (
      <ToastContent
        t={t}
        title={title}
        description={description}
        actionLabel={actionLabel}
        onAction={onAction}
        duration={duration}
        showCheckbox={showCheckbox}
        checkboxKey={checkboxKey}
        config={config}
        Icon={Icon}
      />
    ),
    {
      duration,
      position: "top-right",
    }
  );
};